import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getTheme } from "../theme/modernTheme";
import BrandLogo from "./BrandLogo";
import { navItems, routeActive } from "./sidebarNavConfig";

export { routeActive };

export function findActiveNavCategory(pathname) {
  const section = navItems.find((group) =>
    group.items.some((item) => routeActive(pathname, item.path, item.matchPaths))
  );
  return section?.category ?? null;
}

function NavIcon({ Icon, active, theme, activeFg }) {
  return (
    <Icon
      sx={{
        fontSize: 18,
        color: active ? activeFg : theme.colors.text.muted,
        opacity: active ? 1 : 0.75,
        transition: "color 0.15s ease, opacity 0.15s ease",
      }}
    />
  );
}

function SidebarNavContent({
  pathname,
  navigate,
  currentTheme,
  isNonMobile,
  setIsSidebarOpen,
  onNavigate,
}) {
  const ink = currentTheme.colors.text.primary;
  /** Primary/ink üzerinde okunabilir metin (dark’ta açık zemin → koyu yazı). */
  const onInk = currentTheme.colors.text.button || "#0a0a0a";
  const activeCategory = useMemo(() => findActiveNavCategory(pathname), [pathname]);

  const [openCategory, setOpenCategory] = useState(() => activeCategory);

  useEffect(() => {
    if (activeCategory) setOpenCategory(activeCategory);
  }, [activeCategory]);

  const toggleCategory = (category) => {
    setOpenCategory((prev) => (prev === category ? null : category));
  };

  return (
    <>
      <Box
        sx={{
          flexShrink: 0,
          position: "relative",
          px: 1,
          pt: 2,
          pb: 1.75,
          borderBottom: `1px solid ${currentTheme.colors.border.primary}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!isNonMobile && (
          <IconButton
            size="small"
            onClick={() => setIsSidebarOpen(false)}
            aria-label="Menüyü kapat"
            sx={{
              position: "absolute",
              top: 6,
              right: 4,
              color: currentTheme.colors.text.muted,
              "&:hover": {
                color: currentTheme.colors.text.primary,
                bgcolor: currentTheme.colors.surface.secondary,
              },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
        <BrandLogo
          align="center"
          size={76}
          onClick={() => {
            navigate("/dashboard");
            onNavigate?.();
          }}
          sx={{
            width: "94%",
            maxWidth: 188,
            minHeight: 52,
            borderRadius: 1.5,
            backgroundColor: "transparent",
            // Beyaz zeminli logo: invert → zemin siyaha (sidebar’a karışır), yazı beyaza döner
            filter:
              currentTheme.colors.text.primary === "#fafafa" ? "invert(1)" : "none",
            p: 0,
          }}
        />
      </Box>

      <List
        className="crm-sidebar-nav"
        sx={{
          flex: 1,
          py: 0.75,
          px: 0.5,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {navItems.map(({ category, items }, sectionIdx) => {
          const isOpen = openCategory === category;
          const sectionHasActive = category === activeCategory;

          return (
            <React.Fragment key={category}>
              <ListItem disablePadding sx={{ display: "block" }}>
                <ListItemButton
                  onClick={() => toggleCategory(category)}
                  aria-expanded={isOpen}
                  className={
                    sectionHasActive
                      ? "crm-nav-main crm-nav-main--active"
                      : isOpen
                        ? "crm-nav-main crm-nav-main--open"
                        : "crm-nav-main"
                  }
                  sx={{
                    py: 0.7,
                    px: 1,
                    mx: 0.25,
                    minHeight: 36,
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: sectionHasActive || isOpen
                      ? currentTheme.colors.border.secondary
                      : "transparent",
                    bgcolor: sectionHasActive
                      ? currentTheme.colors.surface.secondary
                      : isOpen
                        ? currentTheme.colors.surface.primary
                        : "transparent",
                    color: sectionHasActive || isOpen
                      ? ink
                      : currentTheme.colors.text.muted,
                    "&:hover": {
                      bgcolor: currentTheme.colors.surface.secondary,
                      color: ink,
                      borderColor: currentTheme.colors.border.primary,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      mr: 1,
                      flexShrink: 0,
                      bgcolor: sectionHasActive
                        ? ink
                        : isOpen
                          ? currentTheme.colors.text.secondary
                          : "transparent",
                      border: sectionHasActive || isOpen
                        ? "none"
                        : `1px solid ${currentTheme.colors.border.primary}`,
                      opacity: sectionHasActive || isOpen ? 1 : 0.45,
                    }}
                  />
                  <ListItemText
                    primary={category}
                    primaryTypographyProps={{
                      fontSize: "0.625rem",
                      fontWeight: sectionHasActive || isOpen ? 800 : 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      lineHeight: 1.2,
                      noWrap: true,
                    }}
                  />
                  <ExpandMoreRoundedIcon
                    sx={{
                      fontSize: 18,
                      opacity: isOpen || sectionHasActive ? 0.95 : 0.55,
                      color: "inherit",
                      transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                      transition: "transform 0.2s ease, opacity 0.15s ease",
                    }}
                  />
                </ListItemButton>
              </ListItem>

              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List
                  disablePadding
                  sx={{
                    pb: 0.35,
                    ml: 0.5,
                    pl: 0.75,
                    borderLeft: `2px solid ${
                      sectionHasActive
                        ? ink
                        : currentTheme.colors.border.primary
                    }`,
                  }}
                >
                  {items.map(({ text, title, path, matchPaths, Icon }) => {
                    const isActive = routeActive(pathname, path, matchPaths);

                    return (
                      <ListItem key={text} disablePadding sx={{ display: "block" }}>
                        <ListItemButton
                          onClick={() => {
                            if (path) {
                              setOpenCategory(category);
                              navigate(path);
                              onNavigate?.();
                            }
                          }}
                          className={isActive ? "crm-nav-sub crm-nav-sub--active" : "crm-nav-sub"}
                          sx={{
                            py: 0.55,
                            pl: 1,
                            pr: 1,
                            mx: 0.25,
                            my: 0.15,
                            minHeight: 34,
                            borderRadius: "8px",
                            gap: 1,
                            bgcolor: isActive ? ink : "transparent",
                            color: isActive ? onInk : currentTheme.colors.text.secondary,
                            boxShadow: isActive ? "0 1px 2px rgba(0,0,0,0.12)" : "none",
                            transition: "background-color 0.15s ease, color 0.15s ease",
                            "&:hover": {
                              bgcolor: isActive ? ink : currentTheme.colors.surface.secondary,
                              color: isActive ? onInk : ink,
                              "& .MuiSvgIcon-root": {
                                opacity: 1,
                                color: isActive ? onInk : ink,
                              },
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 26,
                              mr: 0,
                              justifyContent: "center",
                              color: "inherit",
                            }}
                          >
                            <NavIcon Icon={Icon} active={isActive} theme={currentTheme} activeFg={onInk} />
                          </ListItemIcon>
                          <ListItemText
                            primary={title}
                            primaryTypographyProps={{
                              fontSize: "0.8125rem",
                              fontWeight: isActive ? 700 : 500,
                              lineHeight: 1.35,
                              noWrap: true,
                              color: isActive ? onInk : "inherit",
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>

              {sectionIdx < navItems.length - 1 ? (
                <Divider
                  sx={{
                    my: 0.5,
                    mx: 1,
                    borderColor: currentTheme.colors.border.primary,
                    opacity: 0.35,
                  }}
                />
              ) : null}
            </React.Fragment>
          );
        })}
      </List>
    </>
  );
}

const Sidebar = ({
  embedded = false,
  drawerWidth = "216px",
  isSidebarOpen,
  setIsSidebarOpen,
  isNonMobile,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { mode } = useSelector((state) => state.global);
  const currentTheme = getTheme(mode);

  const navContent = (
    <SidebarNavContent
      pathname={pathname}
      navigate={navigate}
      currentTheme={currentTheme}
      isNonMobile={isNonMobile}
      setIsSidebarOpen={setIsSidebarOpen}
      onNavigate={!isNonMobile ? () => setIsSidebarOpen(false) : undefined}
    />
  );

  if (embedded) {
    return (
      <Box
        className="crm-sidebar"
        sx={{
          width: "100%",
          height: "100%",
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {navContent}
      </Box>
    );
  }

  if (!isSidebarOpen) {
    return null;
  }

  return (
    <Drawer
      open={isSidebarOpen}
      onClose={() => setIsSidebarOpen(false)}
      variant="temporary"
      anchor="left"
      ModalProps={{ keepMounted: true }}
      sx={{
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          position: "relative",
          color: currentTheme.colors.text.secondary,
          background: currentTheme.colors.surface.primary,
          backdropFilter: currentTheme.effects.backdrop,
          borderRight: `1px solid ${currentTheme.colors.border.primary}`,
          boxShadow: currentTheme.shadows.glass,
        },
      }}
    >
      {navContent}
    </Drawer>
  );
};

export default Sidebar;
